import { type CSSProperties } from "react";

export type DotState =
  | "recording"
  | "rendering"
  | "complete"
  | "queued"
  | "offline"
  | "amber"
  | "error";

export interface DotProps {
  state?: DotState;
  size?: number;
  className?: string;
  /** Accessible label; if omitted the dot is treated as decorative. */
  label?: string;
}

/** The Dot — Vocaler's status system. Ported from brand.jsx, faithful to spec. */
export function Dot({ state = "recording", size = 8, className = "", label }: DotProps) {
  const base: CSSProperties = {
    width: size,
    height: size,
    borderRadius: 9999,
    display: "inline-block",
    flexShrink: 0,
  };
  const a11y = label
    ? { role: "img" as const, "aria-label": label }
    : { "aria-hidden": true as const };

  if (state === "recording") {
    return <span className={className} style={{ ...base, background: "#FF2D1F" }} {...a11y} />;
  }
  if (state === "complete") {
    return <span className={className} style={{ ...base, background: "#22C55E" }} {...a11y} />;
  }
  if (state === "queued") {
    return (
      <span
        className={className}
        style={{
          ...base,
          background: "transparent",
          border: "1.5px solid #FF2D1F",
          boxSizing: "border-box",
        }}
        {...a11y}
      />
    );
  }
  if (state === "offline") {
    return (
      <span
        className={className}
        style={{
          ...base,
          background: "transparent",
          border: "1.5px solid #6B6B70",
          boxSizing: "border-box",
        }}
        {...a11y}
      />
    );
  }
  if (state === "amber") {
    return <span className={className} style={{ ...base, background: "#F59E0B" }} {...a11y} />;
  }
  if (state === "error") {
    return (
      <span
        className={className}
        style={{ ...base, background: "#FF2D1F", boxShadow: "0 0 0 1.5px rgba(255,45,31,.5)" }}
        {...a11y}
      />
    );
  }
  // rendering
  return (
    <span
      className={className}
      style={{ ...base, background: "#FF2D1F", position: "relative" }}
      {...a11y}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: 9999,
          background: "#FF2D1F",
          opacity: 0.4,
          animation: "vocPulse 1.2s cubic-bezier(0.16,1,0.3,1) infinite",
        }}
      />
    </span>
  );
}

export interface StatusPillProps {
  state?: DotState;
  children: React.ReactNode;
}

/** Status pill — dot + uppercase micro label. */
export function StatusPill({ state = "rendering", children }: StatusPillProps) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "var(--fg-muted)",
        padding: "4px 10px",
        border: "1px solid var(--border)",
        borderRadius: 9999,
        background: "var(--bg-elev)",
      }}
    >
      <Dot state={state} size={6} />
      {children}
    </span>
  );
}
